import { ReactNode } from 'react';
import { Box, Icon, Link, Text } from '@nimbus-ds/components';
import { LinkIcon, ChevronRightIcon } from '@nimbus-ds/icons';

export interface ChatMarkdownLinkProps {
  href?: string;
  children: ReactNode;
  type: 'internal' | 'external';
  confirmed: boolean;
  onScrollToElement: (target: string) => void;
  onNavigate: (href: string) => void;
}

/**
 * Renders markdown links with appropriate behavior based on link type.
 * External links open in new tabs, while internal links use actionBus navigation.
 * Internal links are only rendered as links when confirmed, otherwise as plain text.
 */
export function ChatMarkdownLink({
  href,
  children,
  type,
  confirmed,
  onScrollToElement,
  onNavigate,
}: Readonly<ChatMarkdownLinkProps>): JSX.Element {
  const defaultLink = <Text as="span">{children}</Text>;

  if (!href) {
    return defaultLink;
  }

  if (type === 'external') {
    return (
      <Link as="a" href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    );
  }

  const isHashLink = href.startsWith('#');
  const isRelativeLink = !isHashLink && href.startsWith('/');

  if (isRelativeLink) {
    return (
      <Box
        display="flex"
        backgroundColor={{
          xs: 'neutral-background',
        }}
        borderRadius="2"
        borderColor={{
          xs: 'transparent',
          hover: 'primary-surface',
          active: 'primary-surfaceHighlight',
        }}
        boxShadow={{
          xs: '2',
        }}
        borderWidth="2"
        borderStyle="solid"
        cursor="pointer"
        flex="0 1 auto"
        gap="2"
        p="2"
        as="button"
        onClick={() => onNavigate(href)}
        width="100%"
      >
        <Box
          backgroundColor="ai-generative-surface"
          height="36px"
          width="36px"
          minHeight="36px"
          minWidth="36px"
          borderRadius="2"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Icon
            color="ai-gradientPurpleHigh"
            source={<LinkIcon size="medium" />}
          />
        </Box>
        <Box
          display="flex"
          flex="1 1 auto"
          flexDirection="column"
          justifyContent="center"
        >
          <Text fontSize="base" color="neutral-textHigh" fontWeight="medium">
            {children}
          </Text>
        </Box>

        <Box alignSelf="center">
          <Icon
            data-testid="callout-card-open-icon"
            color="neutral-textLow"
            source={<ChevronRightIcon size="small" />}
          />
        </Box>
      </Box>
    );
  }

  if (isHashLink && !confirmed) {
    return (
      <Text as="span" fontWeight="medium" color="neutral-textHigh">
        {children}
      </Text>
    );
  }

  return (
    <Box
      as="a"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();

        const target = href.substring(1);
        onScrollToElement(target);
      }}
      textDecoration="underline"
      cursor="pointer"
      color={{ xs: 'neutral-textHigh', active: 'neutral-interactive' }}
    >
      {children}
    </Box>
  );
}

export default ChatMarkdownLink;
